import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { preserveOffsetOnSource } from "@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { RefObject, useEffect, useRef, useState } from "react";

import type { DropEdge } from "./routeLabelsConfig";

// Drag data is untyped at runtime, so sortable items carry a symbol key for
// the type guard to check. The scope keeps items from different lists (the
// groups, or the labels of one group) from being dropped on each other.
type DragData = Record<string | symbol, unknown>;

const sortableKey = Symbol("routeLabelSortable");

type SortableData = { [sortableKey]: true; scope: string; id: string };

const getSortableData = (scope: string, id: string): SortableData => ({
  [sortableKey]: true,
  scope,
  id,
});

const isSortableData = (data: DragData, scope: string): data is SortableData =>
  data[sortableKey] === true && data.scope === scope;

// The rows of the label list, which are a mix of groups and root labels.
export const ROWS_SCOPE = "rows";

type Axis = "vertical" | "horizontal";

const getEdge = (element: Element, input: { clientX: number; clientY: number }, axis: Axis) => {
  const rect = element.getBoundingClientRect();
  const isBefore =
    axis === "vertical"
      ? input.clientY < rect.top + rect.height / 2
      : input.clientX < rect.left + rect.width / 2;
  return isBefore ? "before" : "after";
};

type UseSortableItemOptions = {
  scope: string;
  id: string;
  axis: Axis;
  enabled?: boolean;
  // The part that moves and shows as the drag preview. Defaults to the whole item.
  dragElementRef?: RefObject<HTMLElement>;
  // The part that starts a drag, such as a grip. Defaults to the dragged element.
  handleRef?: RefObject<HTMLElement>;
  // For items with no fill of their own: the browser's drag image is a
  // rectangle, and anything transparent in it comes out white. This draws the
  // item on a solid background in the given shape instead.
  previewShape?: "pill" | "row";
};

// Makes the element on `ref` draggable and a drop target within its scope.
// `edge` is the side a dragged item would land on while it's over this one.
export const useSortableItem = <T extends HTMLElement>({
  scope,
  id,
  axis,
  enabled = true,
  dragElementRef,
  handleRef,
  previewShape,
}: UseSortableItemOptions) => {
  const ref = useRef<T>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [edge, setEdge] = useState<DropEdge | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!enabled || !element) {
      return;
    }
    return combine(
      draggable({
        element: dragElementRef?.current ?? element,
        dragHandle: handleRef?.current ?? undefined,
        getInitialData: () => getSortableData(scope, id),
        onGenerateDragPreview: ({ nativeSetDragImage, location, source }) => {
          if (!previewShape) {
            return;
          }
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({
              element: source.element,
              input: location.current.input,
            }),
            render: ({ container }) => {
              const preview = source.element.cloneNode(true) as HTMLElement;
              // The container sizes to its content, so a full-width row would shrink.
              preview.style.width = `${source.element.getBoundingClientRect().width}px`;
              preview.style.backgroundColor = "var(--joy-palette-background-surface)";
              if (previewShape === "pill") {
                preview.style.borderRadius = "999px";
              } else {
                preview.style.borderRadius = "8px";
                preview.style.border = "1px solid var(--joy-palette-divider)";
                preview.style.boxSizing = "border-box";
              }
              container.appendChild(preview);
            },
          });
        },
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element,
        getData: () => getSortableData(scope, id),
        canDrop: ({ source }) => isSortableData(source.data, scope) && source.data.id !== id,
        onDrag: ({ location }) => setEdge(getEdge(element, location.current.input, axis)),
        onDragLeave: () => setEdge(null),
        onDrop: () => setEdge(null),
      }),
    );
  }, [scope, id, axis, enabled, dragElementRef, handleRef, previewShape]);

  return { ref, isDragging, edge };
};

// Calls `onReorder` when an item from this scope is dropped on another one.
export const useSortableMonitor = (
  scope: string,
  axis: Axis,
  onReorder: (sourceId: string, targetId: string, edge: DropEdge) => void,
) => {
  const onReorderRef = useRef(onReorder);
  useEffect(() => {
    onReorderRef.current = onReorder;
  });

  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) => isSortableData(source.data, scope),
        onDrop: ({ source, location }) => {
          const target = location.current.dropTargets[0];
          if (
            !target ||
            !isSortableData(source.data, scope) ||
            !isSortableData(target.data, scope)
          ) {
            return;
          }
          const edge = getEdge(target.element, location.current.input, axis);
          onReorderRef.current(source.data.id, target.data.id, edge);
        },
      }),
    [scope, axis],
  );
};

// A bar on the side of an item where a dragged item would land. Horizontal
// bars sit in the gap between chips, so the item must be a chip-sized box.
export const dropIndicatorSx = (edge: DropEdge | null, axis: Axis) => {
  if (!edge) {
    return { position: "relative" as const };
  }
  const bar =
    axis === "vertical"
      ? {
          left: 0,
          right: 0,
          height: "2px",
          [edge === "before" ? "top" : "bottom"]: "-1px",
        }
      : {
          top: 0,
          bottom: 0,
          width: "3px",
          [edge === "before" ? "left" : "right"]: "-5.5px",
        };
  return {
    position: "relative" as const,
    "&::before": {
      content: '""',
      position: "absolute" as const,
      zIndex: 1,
      pointerEvents: "none",
      borderRadius: "sm",
      backgroundColor: "primary.500",
      ...bar,
    },
  };
};
